import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { STORAGE_KEYS } from '@/constants/storage';

import { supabase } from '../integrations/supabase/client';
import { getLogoutEpoch } from '../lib/logout-teardown-signal';
import { isSignedOutReadFailure } from '../lib/post-logout-denial';
import { isTransientFetchError } from '../lib/supabase-utils';
import { useAuth } from './auth-context';
import { OrganizationContext } from './organization-context';
import { useOrganizationMembers } from './use-organization-members';
import { useOrganizationSwitching } from './use-organization-switching';

import type { ReactNode } from 'react';
import type {
  Organization,
  OrganizationContextType,
  OrganizationMember,
} from './organization-types';

interface OrganizationProviderProps {
  readonly children: ReactNode;
}

const ORGANIZATION_STORAGE_KEY = STORAGE_KEYS.SELECTED_ORGANIZATION_ID;

export const OrganizationProvider = ({ children }: OrganizationProviderProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string | null>(null);
  const [reconcileRetryTick, setReconcileRetryTick] = useState(0);
  const hasInitializedRef = useRef(false);
  const isSwitchingRef = useRef(false);
  const isDeletingRef = useRef(false);
  const hadUserBeforeRef = useRef(false);

  const setIsSwitching = useCallback((value: boolean) => {
    isSwitchingRef.current = value;
  }, []);
  const setIsDeleting = useCallback((value: boolean) => {
    isDeletingRef.current = value;
  }, []);
  const scheduleCurrentOrganizationId = useCallback((organizationId: string | null) => {
    globalThis.queueMicrotask(() => setCurrentOrganizationId(organizationId));
  }, []);

  const {
    data: userOrganizations = [],
    isLoading: isLoadingOrganizations,
    refetch: refreshOrganizations,
  } = useQuery({
    queryKey: ['user-organizations', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return [];
      }
      const logoutEpoch = getLogoutEpoch();
      const { data, error, status } = await supabase
        .from('organization_members')
        .select(
          `
          id, organization_id, user_id, role, status, joined_at, created_at, updated_at,
          organization:organizations (
            id, name, slug, logo_url, is_active, description, settings, owner_id, created_at, updated_at
          )
        `,
        )
        .eq('user_id', user.id)
        // Pending memberships have no RPC grants; including them produces
        // guaranteed 403s on every email-list / counts / search call. Pending
        // invitations belong on a separate read path (not yet implemented).
        .eq('status', 'active')
        .order('joined_at', { ascending: false });

      if (error) {
        if (!(await isSignedOutReadFailure(error, logoutEpoch, status))) {
          console.error('Error fetching organizations:', error.message);
        }
        throw error;
      }

      return (data || [])
        .map((item: Record<string, unknown>) => ({
          ...item,
          organization: item.organization,
        }))
        .filter(
          (item: Record<string, unknown>) => item.organization !== null,
        ) as OrganizationMember[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const { data: isPlatformAdmin = false } = useQuery({
    queryKey: ['is-platform-admin', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return false;
      }

      const { data, error } = await supabase.rpc('is_admin').single();

      if (error) {
        // A transport blip (navigation aborting the fetch, network reset) is not an
        // application error; console.error routes it into Sentry as one.
        if (!isTransientFetchError(error)) {
          console.error('Error checking platform admin status:', error.message);
        }
        return false;
      }

      return data === true;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const memberHook = useOrganizationMembers(currentOrganizationId, user, isLoadingOrganizations);

  const { switchOrganization, handleOrganizationDeletion } = useOrganizationSwitching({
    user,
    currentOrganizationId,
    setCurrentOrganizationId,
    userOrganizations,
    isPlatformAdmin,
    setIsSwitching,
    setIsDeleting,
  });

  useEffect(() => {
    if (user) {
      hadUserBeforeRef.current = true;
    } else if (hadUserBeforeRef.current) {
      scheduleCurrentOrganizationId(null);
      hasInitializedRef.current = false;
      localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
      hadUserBeforeRef.current = false;
    }
  }, [scheduleCurrentOrganizationId, user]);

  useEffect(() => {
    if (!user || hasInitializedRef.current || userOrganizations.length === 0) {
      return;
    }

    hasInitializedRef.current = true;

    const storedOrgId = localStorage.getItem(ORGANIZATION_STORAGE_KEY);
    const isAdminRoute = location.pathname.startsWith('/admin');

    if (isAdminRoute && isPlatformAdmin) {
      scheduleCurrentOrganizationId('admin');
      return;
    }

    if (storedOrgId === 'admin' && isPlatformAdmin) {
      scheduleCurrentOrganizationId('admin');
      return;
    }

    if (
      storedOrgId &&
      storedOrgId !== 'admin' &&
      userOrganizations.some((om) => om.organization_id === storedOrgId && om.status === 'active')
    ) {
      scheduleCurrentOrganizationId(storedOrgId);
      return;
    }

    if (isPlatformAdmin) {
      scheduleCurrentOrganizationId('admin');
    } else if (userOrganizations.length > 0) {
      scheduleCurrentOrganizationId(userOrganizations[0].organization_id);
    }
  }, [user, userOrganizations, isPlatformAdmin, location.pathname, scheduleCurrentOrganizationId]);

  useEffect(() => {
    if (
      !hasInitializedRef.current ||
      currentOrganizationId === 'admin' ||
      isLoadingOrganizations ||
      !currentOrganizationId
    ) {
      return;
    }

    // The switch/delete flags are refs, invisible to this effect's deps: a
    // membership refetch that resolves inside their window would be skipped
    // here, and TanStack's structural sharing can keep `userOrganizations`
    // referentially stable on the next refetch — the skipped reconciliation
    // would then never re-run, leaving a revoked org selected until reload.
    // Re-arm instead of bailing so the run happens once the window closes.
    if (isSwitchingRef.current || isDeletingRef.current) {
      const retry = setTimeout(() => setReconcileRetryTick((tick) => tick + 1), 150);
      return () => clearTimeout(retry);
    }

    const orgStillExists = userOrganizations.some(
      (om) => om.organization_id === currentOrganizationId,
    );

    if (!orgStillExists && !isDeletingRef.current) {
      isSwitchingRef.current = true;

      if (isPlatformAdmin) {
        scheduleCurrentOrganizationId('admin');
        localStorage.setItem(ORGANIZATION_STORAGE_KEY, 'admin');
      } else if (userOrganizations.length > 0) {
        const newOrgId = userOrganizations[0].organization_id;
        scheduleCurrentOrganizationId(newOrgId);
        localStorage.setItem(ORGANIZATION_STORAGE_KEY, newOrgId);
      } else {
        scheduleCurrentOrganizationId(null);
        localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
      }

      setTimeout(() => {
        isSwitchingRef.current = false;
      }, 100);
    }
  }, [
    userOrganizations,
    currentOrganizationId,
    isLoadingOrganizations,
    isPlatformAdmin,
    scheduleCurrentOrganizationId,
    reconcileRetryTick,
  ]);

  useEffect(() => {
    if (!hasInitializedRef.current || !isPlatformAdmin || isSwitchingRef.current) {
      return;
    }

    const isAdminRoute = location.pathname.startsWith('/admin');
    const isAuthRoute = location.pathname.startsWith('/auth');
    const isExcludedRoute =
      isAuthRoute ||
      location.pathname === '/welcome' ||
      location.pathname === '/onboarding' ||
      location.pathname === '/request-access';

    if (isAdminRoute && currentOrganizationId !== 'admin') {
      scheduleCurrentOrganizationId('admin');
      return;
    }

    if (currentOrganizationId === 'admin' && !isAdminRoute && !isExcludedRoute) {
      Promise.resolve(navigate('/admin')).catch(() => null);
    }
  }, [
    location.pathname,
    isPlatformAdmin,
    currentOrganizationId,
    navigate,
    scheduleCurrentOrganizationId,
  ]);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      return;
    }

    if (currentOrganizationId) {
      localStorage.setItem(ORGANIZATION_STORAGE_KEY, currentOrganizationId);
    } else {
      localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
    }
  }, [currentOrganizationId]);

  const currentOrganization: Organization | null =
    currentOrganizationId === 'admin'
      ? null
      : userOrganizations.find((om) => om.organization_id === currentOrganizationId)
          ?.organization || null;

  const value: OrganizationContextType = useMemo(
    () => ({
      currentOrganization,
      currentOrganizationId,
      userOrganizations,
      isLoadingOrganizations,
      isPlatformAdmin,
      switchOrganization,
      refreshOrganizations: async () => {
        const result = await refreshOrganizations();
        // A transport error must never masquerade as an authoritative empty
        // membership list; invitation and access flows branch on this result.
        if (result.error) {
          throw result.error;
        }
        return result.data ?? [];
      },
      handleOrganizationDeletion,
      members: memberHook.members,
      activeMembers: memberHook.activeMembers,
      isLoadingMembers: memberHook.isLoadingMembers,
      getMemberById: memberHook.getMemberById,
      getMemberByUserId: memberHook.getMemberByUserId,
      getMemberByEmail: memberHook.getMemberByEmail,
      getMemberAvatar: memberHook.getMemberAvatar,
      getMemberDisplayName: memberHook.getMemberDisplayName,
      getMembersByRole: memberHook.getMembersByRole,
      refreshMembers: memberHook.refreshMembers,
      invalidateMembers: memberHook.invalidateMembers,
    }),
    [
      currentOrganization,
      currentOrganizationId,
      userOrganizations,
      isLoadingOrganizations,
      isPlatformAdmin,
      switchOrganization,
      refreshOrganizations,
      handleOrganizationDeletion,
      memberHook,
    ],
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};
